// SPDX-License-Identifier: AGPL-3.0-or-later

import {ResettableStates} from '@app/features/app/state/ResettableStates';
import type {TaskResponse, TaskStatus} from '@fluxer/schema/src/domains/task/TaskSchemas';
import {makeAutoObservable} from 'mobx';

export type Task = TaskResponse;
export type {TaskStatus};

export type TaskDueGroup = 'overdue' | 'today' | 'upcoming' | 'none';

function todayIso(now = new Date()): string {
	const y = now.getFullYear();
	const m = String(now.getMonth() + 1).padStart(2, '0');
	const d = String(now.getDate()).padStart(2, '0');
	return `${y}-${m}-${d}`;
}

export function dueGroupOf(task: Task, now = new Date()): TaskDueGroup {
	if (!task.due_date) return 'none';
	const today = todayIso(now);
	if (task.due_date < today) return 'overdue';
	if (task.due_date === today) return 'today';
	return 'upcoming';
}

/** Everyone on a to-do (older to-dos only have assignee_id). */
export function assigneesOf(task: Pick<Task, 'assignee_id'> & {assignee_ids?: ReadonlyArray<string> | null}): Array<string> {
	if (task.assignee_ids && task.assignee_ids.length > 0) return [...task.assignee_ids];
	return task.assignee_id ? [task.assignee_id] : [];
}

export function matchesTaskQuery(task: Task, query: string): boolean {
	const q = query.trim().toLowerCase();
	if (!q) return true;
	return task.title.toLowerCase().includes(q) || (task.notes ?? '').toLowerCase().includes(q);
}

class Tasks {
	private tasksByGuild = new Map<string, Map<string, Task>>();
	private loadedGuilds = new Set<string>();
	private loadingGuilds = new Set<string>();

	constructor() {
		makeAutoObservable(this, {}, {autoBind: true});
	}

	isLoaded(guildId: string): boolean {
		return this.loadedGuilds.has(guildId);
	}

	isLoading(guildId: string): boolean {
		return this.loadingGuilds.has(guildId);
	}

	/** Every to-do in the guild, archived ones included. */
	getAllGuildTasks(guildId: string): Array<Task> {
		const tasks = this.tasksByGuild.get(guildId);
		if (!tasks) return [];
		return Array.from(tasks.values()).sort((a, b) => a.created_at.localeCompare(b.created_at));
	}

	/** To-dos still in play: archived ones are left out of the board and every list. */
	getGuildTasks(guildId: string): Array<Task> {
		return this.getAllGuildTasks(guildId).filter((task) => !task.archived_at);
	}

	getArchivedTasks(guildId: string): Array<Task> {
		return this.getAllGuildTasks(guildId)
			.filter((task) => task.archived_at)
			.sort((a, b) => (b.archived_at ?? '').localeCompare(a.archived_at ?? ''));
	}

	getChannelTasks(guildId: string, channelId: string): Array<Task> {
		return this.getGuildTasks(guildId).filter((task) => task.channel_id === channelId);
	}

	getMyTasks(guildId: string, userId: string): Array<Task> {
		return this.getGuildTasks(guildId).filter((task) => assigneesOf(task).includes(userId));
	}

	getTaskForMessage(messageId: string): Task | null {
		for (const tasks of this.tasksByGuild.values()) {
			for (const task of tasks.values()) {
				if (task.message_id === messageId) return task;
			}
		}
		return null;
	}

	handleLoadStart(guildId: string): void {
		this.loadingGuilds.add(guildId);
	}

	handleLoadSuccess(guildId: string, tasks: ReadonlyArray<Task>): void {
		this.tasksByGuild.set(guildId, new Map(tasks.map((task) => [task.id, task])));
		this.loadedGuilds.add(guildId);
		this.loadingGuilds.delete(guildId);
	}

	handleLoadError(guildId: string): void {
		this.loadingGuilds.delete(guildId);
	}

	upsert(task: Task): void {
		let tasks = this.tasksByGuild.get(task.guild_id);
		if (!tasks) {
			tasks = new Map();
			this.tasksByGuild.set(task.guild_id, tasks);
		}
		tasks.set(task.id, task);
	}

	remove(guildId: string, taskId: string): void {
		this.tasksByGuild.get(guildId)?.delete(taskId);
	}

	handleGatewayReady(): void {
		this.tasksByGuild = new Map();
		this.loadedGuilds = new Set();
		this.loadingGuilds = new Set();
	}
}

const tasks = new Tasks();
ResettableStates.register(tasks, tasks.handleGatewayReady);
export default tasks;
